import * as THREE from 'three';
import type { ReferencePlane } from '../../../../../reference/ReferencePlane';
import { buildArcFromThreePoints2D } from '../../../../../tool/buildArcFromThreePoints2D';
import { isAngleBetweenCCW } from '../../../../../tool/isAngleBetweenCCW';
import { normalizeAngle } from '../../../../../tool/normalizeAngle';
import { normalizeArcAngles } from '../../../../../tool/normalizeArcAngles';
import type { ArcProjectionResult } from '../../../model/ProjectionResult';
import type { ResolvedArcReferenceSource } from '../SketchReferenceSourceResolver';
import type { SupportProjectionPreprocessor } from './SupportProjectionPreprocessor';
import {
	buildProjectedPlanarBasis,
	decomposeProjectedEllipseBasis,
	isProjectedCircle,
	isProjectedLine,
	localPointToWorldCoordinate,
	resolveEllipseParameterAngle,
	resolveProjectedLocalPointByParameter,
	PROJECTION_EPSILON,
} from './SupportProjectionPreprocessor';

/**
 * 圆弧源投影预处理器。
 * 说明：
 * 1. 与圆投影共享同一条二维线性映射基线；
 * 2. 非平行场景下可输出 ellipse-arc，而不是直接失败；
 * 3. 当短轴退化时，会闭合为 line，而不是留下灰区；
 * 4. ellipse-arc 的起止角/方向由投影后的起点、终点、中点反解，保证与目标平面坐标系一致。
 */
export class ArcSupportProjectionPreprocessor
	implements SupportProjectionPreprocessor<ResolvedArcReferenceSource, ArcProjectionResult>
{
	/**
	 * 执行圆弧投影。
	 *
	 * @param input 投影输入。
	 * @returns 成功时返回 arc / ellipse-arc / line，完全退化时返回 null。
	 */
	public projectToTargetPlane(input: {
		source: ResolvedArcReferenceSource;
		targetReferencePlane: ReferencePlane;
	}): ArcProjectionResult | null {
		const projectedBasis = buildProjectedPlanarBasis({
			centerWorld: input.source.centerWorld,
			planeXAxisWorld: input.source.planeXAxisWorld,
			planeYAxisWorld: input.source.planeYAxisWorld,
			radiusX: input.source.radius,
			radiusY: input.source.radius,
			targetReferencePlane: input.targetReferencePlane,
		});
		const ellipse = decomposeProjectedEllipseBasis(projectedBasis);
		if (!ellipse) {
			return null;
		}

		const projectedStartLocal = resolveProjectedLocalPointByParameter(
			projectedBasis,
			input.source.startAngle,
		);
		const projectedEndLocal = resolveProjectedLocalPointByParameter(
			projectedBasis,
			input.source.endAngle,
		);
		const projectedMidLocal = resolveProjectedLocalPointByParameter(
			projectedBasis,
			this.resolveArcMidParameter(input.source),
		);

		if (isProjectedLine(ellipse)) {
			return this.buildLineProjectionResult({
				source: input.source,
				ellipse,
				projectedBasis,
				targetReferencePlane: input.targetReferencePlane,
			});
		}

		if (isProjectedCircle(ellipse)) {
			const arcDefinition = buildArcFromThreePoints2D(
				projectedStartLocal,
				projectedEndLocal,
				projectedMidLocal,
			);
			if (!arcDefinition || arcDefinition.radius <= PROJECTION_EPSILON) {
				return null;
			}

			return {
				sourceDescriptor: input.source.sourceDescriptor,
				sourceKind: 'arc',
				projectionResultKind: 'arc',
				diagnostics: [],
				geometryPayload: {
					centerWorld: localPointToWorldCoordinate(arcDefinition.center, input.targetReferencePlane),
					radius: arcDefinition.radius,
					startAngle: arcDefinition.startAngle,
					endAngle: arcDefinition.endAngle,
					clockwise: arcDefinition.clockwise,
				},
			};
		}

		const startParameter = resolveEllipseParameterAngle(projectedStartLocal, ellipse);
		const endParameter = resolveEllipseParameterAngle(projectedEndLocal, ellipse);
		const midParameter = resolveEllipseParameterAngle(projectedMidLocal, ellipse);
		if (startParameter === null || endParameter === null || midParameter === null) {
			return null;
		}

		const normalizedStart = normalizeAngle(startParameter);
		const normalizedEnd = normalizeAngle(endParameter);
		const normalizedMid = normalizeAngle(midParameter);
		const clockwise = !isAngleBetweenCCW(normalizedStart, normalizedEnd, normalizedMid);
		const normalizedAngles = normalizeArcAngles(normalizedStart, normalizedEnd, clockwise);

		return {
			sourceDescriptor: input.source.sourceDescriptor,
			sourceKind: 'arc',
			projectionResultKind: 'ellipse-arc',
			diagnostics: [],
			geometryPayload: {
				centerWorld: localPointToWorldCoordinate(ellipse.centerLocal, input.targetReferencePlane),
				radiusX: ellipse.radiusX,
				radiusY: ellipse.radiusY,
				rotation: ellipse.rotation,
				startAngle: normalizedAngles.start,
				endAngle: normalizedAngles.end,
				clockwise,
			},
		};
	}

	/**
	 * 解析圆弧中点参数角。
	 *
	 * @param source 圆弧来源快照。
	 * @returns 中点对应的源参数角。
	 */
	private resolveArcMidParameter(source: ResolvedArcReferenceSource): number {
		const normalized = normalizeArcAngles(
			source.startAngle,
			source.endAngle,
			source.clockwise,
		);
		return source.clockwise
			? normalized.start - normalized.angleRange * 0.5
			: normalized.start + normalized.angleRange * 0.5;
	}

	/**
	 * 构造 arc -> line 的退化投影结果。
	 * 说明：
	 * 1. 退化后并不是简单使用投影后的起终点；
	 * 2. 需要沿主轴求整个圆弧在投影线上的有效区间，避免遗漏内部极值；
	 * 3. 只有这样才能闭合半圆、超过 180° 圆弧等边缘情况。
	 *
	 * @param input 线退化构造输入。
	 * @returns 线投影结果。
	 */
	private buildLineProjectionResult(input: {
		source: ResolvedArcReferenceSource;
		ellipse: import('./SupportProjectionPreprocessor').DecomposedProjectedEllipse;
		projectedBasis: import('./SupportProjectionPreprocessor').ProjectedPlanarBasis;
		targetReferencePlane: ReferencePlane;
	}): ArcProjectionResult {
		const phase = Math.atan2(
			input.projectedBasis.axisYLocal.dot(input.ellipse.majorAxisUnitLocal),
			input.projectedBasis.axisXLocal.dot(input.ellipse.majorAxisUnitLocal),
		);
		const candidateParameters = [
			input.source.startAngle,
			input.source.endAngle,
			this.resolveArcMidParameter(input.source),
		];
		const normalizedSweep = normalizeArcAngles(
			normalizeAngle(input.source.startAngle),
			normalizeAngle(input.source.endAngle),
			input.source.clockwise,
		);
		[phase, phase + Math.PI].forEach((candidate) => {
			if (this.isParameterWithinArcSweep(candidate, normalizedSweep.start, normalizedSweep.end, input.source.clockwise)) {
				candidateParameters.push(candidate);
			}
		});

		const scalars = candidateParameters.map((parameter) => {
			const projectedPoint = resolveProjectedLocalPointByParameter(input.projectedBasis, parameter);
			return projectedPoint.clone().sub(input.ellipse.centerLocal)
				.dot(input.ellipse.majorAxisUnitLocal);
		});
		const startScalar = Math.min(...scalars);
		const endScalar = Math.max(...scalars);
		const startLocal = input.ellipse.centerLocal.clone()
			.addScaledVector(input.ellipse.majorAxisUnitLocal, startScalar);
		const endLocal = input.ellipse.centerLocal.clone()
			.addScaledVector(input.ellipse.majorAxisUnitLocal, endScalar);

		return {
			sourceDescriptor: input.source.sourceDescriptor,
			sourceKind: 'arc',
			projectionResultKind: 'line',
			diagnostics: [],
			geometryPayload: {
				startWorld: localPointToWorldCoordinate(startLocal, input.targetReferencePlane),
				endWorld: localPointToWorldCoordinate(endLocal, input.targetReferencePlane),
			},
		};
	}

	/**
	 * 判断参数角是否落在圆弧扫掠范围内。
	 *
	 * @param parameter 待判断参数角。
	 * @param normalizedStart 归一化后的起始角。
	 * @param normalizedEnd 归一化后的终止角。
	 * @param clockwise 是否顺时针。
	 * @returns 命中圆弧扫掠范围时返回 true。
	 */
	private isParameterWithinArcSweep(
		parameter: number,
		normalizedStart: number,
		normalizedEnd: number,
		clockwise: boolean,
	): boolean {
		const normalizedParameter = normalizeAngle(parameter);
		if (!clockwise) {
			return isAngleBetweenCCW(normalizedStart, normalizedEnd, normalizedParameter);
		}

		return isAngleBetweenCCW(normalizedEnd, normalizedStart, normalizedParameter);
	}
}
